import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  type ReactNode,
} from 'react';
import { createElement } from 'react';
import { Lang } from './lib/i18n';

const STORAGE_KEY = 'firetrack-web-lang';

function getInitialLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && (Object.values(Lang) as string[]).includes(stored)) {
      return stored as Lang;
    }
  } catch {
    /* SSR / private browsing */
  }
  return Lang.EN;
}

interface LangCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
}

const LangContext = createContext<LangCtx>({
  lang: Lang.EN,
  setLang: () => {},
});

export function LangProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(getInitialLang);

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* noop */
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return createElement(
    LangContext.Provider,
    { value: { lang, setLang } },
    children
  );
}

export function useLang() {
  return useContext(LangContext);
}
